import { useEffect, useMemo, useRef } from 'react';

type DebouncedFn<T extends (...args: any[]) => any> = ((
  ...args: Parameters<T>
) => void) & {
  /** Cancels the pending invocation, if any. */
  cancel: () => void;
};

export const INPUT_DEBOUNCE_TIMEOUT = 400;

export function useDebounce<T extends (...args: any[]) => any>(
  callback: T,
  delay: number,
  dependencies: readonly unknown[],
): DebouncedFn<T> {
  const callbackRef = useRef(callback);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback, ...dependencies]);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const debouncedFn = useMemo<DebouncedFn<T>>(
    () =>
      Object.assign(
        (...args: Parameters<T>) => {
          if (timerRef.current) {
            clearTimeout(timerRef.current);
          }

          timerRef.current = setTimeout(() => {
            callbackRef.current(...args);
          }, delay);
        },
        {
          cancel: () => {
            if (timerRef.current) {
              clearTimeout(timerRef.current);
            }
          },
        },
      ),
    [delay, ...dependencies],
  );

  return debouncedFn;
}
